export type AutotranslatorSpeechFrame = {
    speech: boolean;
    noiseFloor: number;
    threshold: number;
    ended: boolean;
    started: boolean;
};

// Oversampled waveshaping can reconstruct a peak above the curve itself.
// Leave half a decibel after it; do not lower the detector or add more mic gain.
export const AUTOTRANSLATOR_OUTPUT_GAIN = 0.94;

// Deltas remain live between commits. Prefer a real pause, with a bounded
// fallback for rooms whose background sound never falls below the detector.
export const getAutotranslatorCommitReason = (state: {
    now: number; speechStartedAt: number; lastSpeechAt: number;
    lastCommitAt: number; lastCommittedSpeechAt: number;
}): "pause" | "max_duration" | null => {
    const { now, speechStartedAt, lastSpeechAt, lastCommitAt, lastCommittedSpeechAt } = state;
    if (!speechStartedAt || !lastSpeechAt || lastSpeechAt <= lastCommittedSpeechAt) return null;
    const bufferedMs = now - Math.max(lastCommitAt, speechStartedAt);
    if (bufferedMs < 820) return null;
    if (now - lastSpeechAt >= 420) return "pause";
    return bufferedMs >= 8000 ? "max_duration" : null;
};

export const createAutotranslatorLimiterCurve = (): Float32Array<ArrayBuffer> => {
    const curve = new Float32Array(4097);
    for (let index = 0; index < curve.length; index++) {
        const input = index * 2 / (curve.length - 1) - 1;
        const magnitude = Math.abs(input);
        curve[index] = magnitude <= 0.8 ? input :
            Math.sign(input) * (0.8 + 0.18 * (1 - Math.exp(-(magnitude - 0.8) / 0.18)));
    }
    return curve;
};

// Detect on the unamplified speech band. A rolling lower percentile follows
// steady room noise without allowing a loud speaker to set the next threshold.
export const createAutotranslatorSpeechDetector = () => {
    let samples: Array<{ time: number; rms: number }> = [];
    let lastSampleAt = 0;
    let lastSpeechAt = 0;
    let active = false;
    let noiseFloor = 0;
    let audibleStartedAt = 0;
    return {
        update(rms: number, now: number, minimum = 0.001): AutotranslatorSpeechFrame {
            if (!audibleStartedAt && rms >= minimum * 0.25) audibleStartedAt = now;
            if (!lastSampleAt || now - lastSampleAt >= 40) {
                samples.push({ time: now, rms });
                samples = samples.filter(sample => now - sample.time <= 2400);
                lastSampleAt = now;
                const sorted = samples.filter(sample => audibleStartedAt && sample.time >= audibleStartedAt)
                    .map(sample => sample.rms).sort((a, b) => a - b);
                const observedFloor = sorted[Math.floor((sorted.length - 1) * 0.2)] ?? 0;
                if (!audibleStartedAt || now - audibleStartedAt < 320) {
                    // AudioContext starts with a few zero-filled frames. Do not
                    // let those label the actual café floor as sustained speech.
                    const audible = sorted.filter(value => value >= minimum * 0.25);
                    noiseFloor = audible[Math.floor((audible.length - 1) * 0.2)] ?? 0;
                } else {
                    // Speech cannot train the floor upward and hide the next
                    // quieter speaker; quieter observations may still lower it.
                    noiseFloor = samples.length === 1 ? observedFloor : observedFloor < noiseFloor ? observedFloor :
                        noiseFloor + (observedFloor - noiseFloor) * (active ? 0 : 0.18);
                }
            }
            const threshold = Math.max(minimum, noiseFloor * (active ? 1.65 : 2.1));
            if (!audibleStartedAt || now - audibleStartedAt < 320) return { speech: false, started: false, ended: false, noiseFloor, threshold };
            const speech = rms >= threshold;
            const started = speech && !active;
            if (speech) { active = true; lastSpeechAt = now; }
            const ended = active && !speech && now - lastSpeechAt >= 420;
            if (ended) active = false;
            return { speech, started, ended, noiseFloor, threshold };
        }
    };
};
